import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import CustomLink from '@/components/general/CustomLink';
import CustomTable from '@/components/general/CustomTable';
import CustomTableCell from '@/components/general/CustomTableCell';
import CustomTableRow from '@/components/general/CustomTableRow';
import CustomTableToolbar from '@/components/general/CustomTableToolbar';
import PageTitle from '@/components/general/PageTitle';
import { getEndpointWithParams } from '@/routes/config';
import { HeaderTable } from '@/types';
import { UsersCheckinsSummary } from '@/types/api';
import { Paths } from '@/types/routes';
import { numberToPercent } from '@/utils/format';

type UsersPageProps = {
  users: UsersCheckinsSummary[];
};

const UsersPage = function ({ users }: UsersPageProps): JSX.Element {
  const arrayHeaders: HeaderTable<UsersCheckinsSummary>[] = useMemo(
    () => [
      { id: 'userName', label: 'Name' },
      { id: 'avgSubmissionRate', label: 'Average Submission' },
      { id: 'checkinsSubmitted', label: 'Check-Ins Submitted' },
    ],
    []
  );

  const renderUserRow = useCallback((row: UsersCheckinsSummary) => {
    const endpoint = getEndpointWithParams(Paths.USER_CHECKINS, { userId: row.userId });

    return (
      <CustomTableRow key={row.userId}>
        <CustomTableCell>
          <CustomLink to={endpoint} data-testid="user-name-clickable">
            {row.userName}
          </CustomLink>
        </CustomTableCell>
        <CustomTableCell data-testid="user-avg-checkins">
          {numberToPercent(row.avgSubmissionRate)}
        </CustomTableCell>
        <CustomTableCell data-testid="user-submitted-checkins">
          {row.checkinsSubmitted} out of {row.existingCheckins}
        </CustomTableCell>
      </CustomTableRow>
    );
  }, []);

  return (
    <Box data-testid="users-list-page">
      <PageTitle title="Users" />

      <CustomTable<UsersCheckinsSummary>
        data={users}
        headersArray={arrayHeaders}
        initialOrderBy="checkinsSubmitted"
        rowRender={renderUserRow}
        toolbar={<CustomTableToolbar title="Users" dataLength={users.length} />}
        data-testid="users-list-table"
      />
    </Box>
  );
};

export default UsersPage;
